import { toastr } from 'react-redux-toastr';
import i18next from 'i18next';
import { isUndefined } from 'underscore';

import { urls } from '@/config/urls';
import { instance } from '@/config/axios';
import { errorConfig } from '../config/i18n';


let settingsAbortController;
export { settingsAbortController };
export const { server } = urls;

export default {
  getPostalCodesByInput(data) {
    return instance
      .post(`${server.settings.postalcodes_lookup}`, data)
      .catch((error) => Promise.reject(error));
  },

  assignPostalCodeIds(territoryID, postal_code_ids) {
    const url = `${server.settings.territories}${territoryID}/postal_codes`;
    return instance
      .post(url, postal_code_ids)
      .catch((error) => Promise.reject(error));
  },

  renameTerritoryName(territoryId, newName) {
    return instance
      .patch(`${server.settings.territories}${territoryId}`, {
        new_territory_name: newName,
      })
      .catch((error) => Promise.reject(error));
  },

  addArea(plzObj) {
    return instance.post(server.settings.areafromplz, plzObj).catch((error) => {
      return Promise.reject(error);
    });
  },

  getAreaFromPoint(obj) {
    const url = `${server.settings.areafrompoint}?lat=${obj.lat}&lon=${obj.lon}`;
    return instance.get(url).catch((error) => {
      return Promise.reject(error);
    });
  },

  renameArea(name, id) {
    const url = `${server.settings.tourarea}/${id}`;
    return instance.patch(url, { areaname: name }).catch((error) => {
      toastr.error(
        i18next.t('common:settings.areaHeader'),
        error.response.data.message,
        errorConfig
      );
      return Promise.reject(error);
    });
  },

  deleteArea(id) {
    const url = server.settings.deleteArea + id;
    return instance.delete(url).catch((error) => {
      toastr.error(
        i18next.t('common:settings.area'),
        i18next.t('common:areaEditor.addAreaErr'),
        errorConfig
      );
      return Promise.reject(error);
    });
  },

  assignDriverToArea(area, driver) {
    return instance
      .patch(`${server.settings.tourarea}/${area}`, { driver_id: driver })
      .catch((error) => {
        toastr.error(
          i18next.t('common:settings.areaHeader'),
          i18next.t('common:routes.group.driverCouldntRemoved'),
          errorConfig
        );
        return Promise.reject(error);
      });
  },

  fetchTourAreas() {
    return instance.get(server.settings.tourarea).catch((error) => {
      return Promise.reject(error);
    });
  },

  getZipCodes(paging, mask) {
    settingsAbortController = new AbortController();
    return instance
      .get(
        `${server.settings.zips}?results_per_page=${paging.results_per_page}&page=${paging.page}`,
        {
          signal: settingsAbortController.signal,
          ...(!isUndefined(mask) ? { headers: { 'X-Fields': mask } } : {}),
        }
      )
      .catch((error) => {
        return Promise.reject(error);
      });
  },

  createTerritory(data) {
    settingsAbortController = new AbortController();
    return instance
      .post(`${server.settings.territories}`, data, {
        signal: settingsAbortController.signal,
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  },

  deleteTerritory(id) {
    return instance
      .delete(`${server.settings.territories}${id}`)
      .catch((error) => {
        return Promise.reject(error);
      });
  },

  patchTerritory(data, id) {
    return instance
      .patch(`${server.settings.territories}/${id}`, data)
      .catch((error) => {
        if (error) {
          return Promise.reject(error);
        }
      });
  },

  assignZip(action, zips, territoryID) {
    const url = `${server.settings.territories}${territoryID}/postal_codes`;

    // DELETE must send body via { data: ... }
    if (action === 'delete') {
      return instance
        .delete(url, {
          data: zips,
        })
        .catch((error) => Promise.reject(error));
    }

    // POST (add postal codes)
    return instance.post(url, zips).catch((error) => Promise.reject(error));
  },

  assignVehicle(action, vehicle, territoryID) {
    const data =
      action === 'delete'
        ? { data: { vehicle_custom_ids: vehicle } }
        : { vehicle_custom_ids: vehicle };
    return instance[action](
      `${server.settings.territories}/${territoryID}/vehicles`,
      data
    ).catch((error) => {
      return Promise.reject(error);
    });
  },

  getTerritory(id) {
    settingsAbortController = new AbortController();
    return instance
      .get(`${server.settings.territories}${id}`, {
        signal: settingsAbortController.signal,
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  },
  getTerritories({ page, page_size } = {}) {
    settingsAbortController = new AbortController();

    return instance
      .get(
        `${server.settings.territories}?page=${page}&page_size=${page_size}`,
        { signal: settingsAbortController.signal }
      )
      .catch((error) => {
        return Promise.reject(error);
      });
  },
  addZipCode(zip) {
    settingsAbortController = new AbortController();
    return instance
      .post(server.settings.zips, zip, {
        signal: settingsAbortController.signal,
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  },

  getAreabyId(id) {
    return instance.get(`${server.settings.tourarea}/${id}`);
  },

  importHeader(cb) {
    instance
      .get(server.settings.importHeaderData, {})
      .then((response) => {
        // console.log("Call from import header success")
        const { data } = response;
        if (cb) cb(data);
      })
      .catch((error) => {
        return Promise.reject(error);
      });
  },

  importHeaderKeys(cb) {
    instance
      .get(server.settings.importHeaderKeysData, {})
      .then((response) => {
        const { data } = response;
        if (cb) cb(data);
      })
      .catch((error) => {
        console.log('Call from import header keys fail');
        console.log(error);
      });
  },

  exportHeader(obj) {
    return instance
      .post(server.settings.exportHeaderData, obj)
      .then((response) => response)
      .catch((error) => {
        return Promise.reject(error);
      });
  },

  getAccounting() {
    return instance.get(server.accounting).catch((error) => {
      throw error;
    });
  },

  getFrontendSettings() {
    return instance.get(server.settings.frontend).catch((error) => {
      return Promise.reject(error);
    });
  },

  updateFrontendSettings(data) {
    return instance.patch(server.settings.frontend, data).catch((error) => {
      return Promise.reject(error);
    });
  },
};
